import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  FiHome, FiClipboard, FiUsers, FiUserCheck, 
  FiPackage, FiDollarSign, FiBarChart2,
  FiSun, FiMoon, FiLogOut, FiMenu, FiX,
  FiTruck, FiTag, FiPlus, FiList, FiGrid,
  FiChevronDown, FiChevronRight, FiUser,
  FiBell, FiSettings
} from 'react-icons/fi';
import { LuCoffee } from 'react-icons/lu';
import ConnectionStatus from './ConnectionStatus';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({
    workOrders: false,
    inventory: true,
    customers: false,
    staff: false,
    transactions: false,
    reports: false,
  });
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    localStorage.removeItem('eos7-auth');
    navigate('/login');
  };

  //Menu items dengan sub-menu
  const menuItems = [
    {
      path: '/',
      icon: FiHome,
      label: 'Dashboard',
      type: 'single'
    },
    {
      icon: FiClipboard,
      label: 'Work Orders',
      key: 'workOrders',
      type: 'dropdown',
      children: [
        { path: '/work-orders', icon: FiList, label: 'All Orders' },
        { path: '/work-orders/new', icon: FiPlus, label: 'New Order' },
      ]
    },
    {
      icon: FiUsers,
      label: 'Customers',
      key: 'customers',
      type: 'dropdown',
      children: [
        { path: '/customers', icon: FiList, label: 'All Customers' },
        { path: '/customers/new', icon: FiPlus, label: 'Add Customer' },
      ]
    },
    {
      icon: FiUserCheck,
      label: 'Staff',
      key: 'staff',
      type: 'dropdown',
      children: [
        { path: '/staff', icon: FiList, label: 'All Staff' },
        { path: '/staff/new', icon: FiPlus, label: 'Add Staff' },
      ]
    },
    {
      icon: FiPackage,
      label: 'Inventory',
      key: 'inventory',
      type: 'dropdown',
      children: [
        { path: '/inventory', icon: FiList, label: 'All Items' },
        { path: '/inventory/new', icon: FiPlus, label: 'Add Item' },
        { path: '/categories', icon: FiTag, label: 'Categories' },
        { path: '/suppliers', icon: FiTruck, label: 'Suppliers' },
      ]
    },
    {
      icon: FiDollarSign,
      label: 'Kasir',
      key: 'transactions',
      type: 'dropdown',
      children: [
        { path: '/transactions', icon: FiList, label: 'All Transactions' },
        { path: '/transactions/new', icon: FiPlus, label: 'New Transaction' },
      ]
    },
    {
      path: '/reports',
      icon: FiBarChart2,
      label: 'Laporan',
      type: 'single'
    },
 
    // MENU SETTINGS
    {
      path: '/settings',
      icon: FiSettings,
      label: 'Settings',
      type: 'single'
    },
  ];

  const renderMenuItem = (item) => {
    const Icon = item.icon;

    if (item.type === 'single') {
      return (
        <Link
          key={item.path}
          to={item.path}
          className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
            isActive(item.path)
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
              : 'text-gray-300 hover:bg-blue-600/20 hover:text-white'
          }`}
        >
          <Icon size={14} />
          {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
        </Link>
      );
    }

    const isExpanded = expandedMenus[item.key];
    const hasActiveChild = item.children.some(child => isActive(child.path));

    return (
      <div key={item.key} className="space-y-1">
        {/* Parent Menu */}
        <button
          onClick={() => toggleMenu(item.key)}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 ${
            hasActiveChild
              ? 'bg-blue-600/10 text-blue-400'
              : 'text-gray-300 hover:bg-blue-600/20 hover:text-white'
          }`}
        >
          <div className="flex items-center space-x-3">
            <Icon size={18} />
            {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
          </div>
          {sidebarOpen && (
            <span className="text-gray-400">
              {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            </span>
          )}
        </button>

        {/* Children */}
        {isExpanded && sidebarOpen && (
          <div className="ml-6 space-y-1 border-l-2 border-blue-600/20 pl-3">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                    isActive(child.path)
                      ? 'bg-blue-600/20 text-blue-400'
                      : 'text-gray-400 hover:text-white hover:bg-blue-600/10'
                  }`}
                >
                  <ChildIcon size={14} />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-[var(--bg-secondary)]">
   
      {/* SIDEBAR */}
      <div 
        className={`${
          sidebarOpen ? 'w-72' : 'w-20'
        } bg-[var(--bg-sidebar)] transition-all duration-300 flex flex-col shadow-2xl shadow-black/20`}
      >
       {/* Header */}
<div className="p-4 flex items-center justify-between border-b border-[var(--border-color)]">
  {sidebarOpen ? (
    <div>
      <h1
        className="text-white font-extrabold text-xl tracking-wider"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        ELNINO
      </h1>
      <p className="text-[10px] text-gray-400 tracking-widest uppercase">
        TECHNICAL WORK MANAGEMENT
      </p>
    </div>
  ) : (
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-blue-500/20 mx-auto">
      E
    </div>
  )}

  <button
    onClick={() => setSidebarOpen(!sidebarOpen)}
    className="text-gray-400 hover:text-white transition-colors"
  >
    {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
  </button>
</div>

        {/* Menu */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map(renderMenuItem)}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-[var(--border-color)] p-3 space-y-2">
          {/* User Profile */}
          <div className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-blue-600/10">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
              A
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">Admin</p>
                <p className="text-[10px] text-gray-400 truncate">admin@eos7.com</p>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <div className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-blue-600/10 transition-colors">
            <div className="flex items-center space-x-3">
              {theme === 'dark' ? (
                <FiSun size={18} className="text-yellow-400" />
              ) : (
                <FiMoon size={18} className="text-gray-400" />
              )}
              {sidebarOpen && (
                <span className="text-sm text-gray-300">
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </span>
              )}
            </div>
            
            {/* Switch */}
            <button
              onClick={toggleTheme}
              className="relative w-10 h-5 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[var(--bg-sidebar)]"
              style={{
                backgroundColor: theme === 'dark' ? '#4B5563' : '#3B82F6',
              }}
              role="switch"
              aria-checked={theme === 'dark'}
            >
              <span
                className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 flex items-center justify-center"
                style={{
                  transform: theme === 'dark' ? 'translateX(20px)' : 'translateX(0)',
                }}
              >
                {theme === 'dark' ? (
                  <FiMoon size={8} className="text-gray-600" />
                ) : (
                  <FiSun size={8} className="text-yellow-500" />
                )}
              </span>
            </button>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-600/10 w-full transition-all duration-200"
          >
            <FiLogOut size={18} />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </div>

     
      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <Outlet />
        </div>
      
        {/* CONNECTION STATUS */}
        <ConnectionStatus />
      
	  
	  {/* FOOTER */}
        <footer className="border-t border-[var(--border-color)] py-4 px-6 mt-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <span>© {new Date().getFullYear()} AKASIAXWORK</span>
              <span className="hidden md:inline">|</span>
              <span className="flex items-center gap-1">
                Dibuat dengan
                <LuCoffee size={14} className="text-amber-600 animate-pulse" />
                dan kode
              </span>
            </div>
            <div className="text-xs text-[var(--text-secondary)]">
              v1.0.0
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;